import { FC, FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link as RouterLink, useLoaderData } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@oasisprotocol/ui-library/src/components/cards'
import { Button } from '@oasisprotocol/ui-library/src/components/ui/button'
import { Link } from '@oasisprotocol/ui-library/src/components/link'
import { Select, SelectOption } from '@oasisprotocol/ui-library/src/components/select'
import { PageLayout } from '../../components/PageLayout'
import { useRuntimeScope } from '../../hooks/useScopeParam'
import { AddressLoaderData, RouteUtils } from '../../utils/route-utils'
import { getSourcifyServerUrl } from '../../utils/sourcify'
import {
  PreparedVerification,
  preferProjectContracts,
  prepareVerification,
  SolcJsonContract,
} from './prepareVerifyFiles'

const CHAIN_ID = String(import.meta.env.REACT_APP_EVM_CHAIN_ID || 186)

type VerifyResult = {
  ok: boolean
  message: string
  status?: string
}

function contractOptionValue(c: SolcJsonContract): string {
  return `${c.path}::${c.name}`
}

function parseContractOption(value: string): SolcJsonContract | null {
  const idx = value.lastIndexOf('::')
  if (idx <= 0) return null
  return { path: value.slice(0, idx), name: value.slice(idx + 2) }
}

export const VerifyContractPage: FC = () => {
  const { t } = useTranslation()
  const scope = useRuntimeScope()
  const { address } = useLoaderData() as AddressLoaderData

  const [files, setFiles] = useState<File[]>([])
  const [prepared, setPrepared] = useState<PreparedVerification | null>(null)
  const [contractKey, setContractKey] = useState('')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<VerifyResult | null>(null)
  const filesInputRef = useRef<HTMLInputElement>(null)

  const tokenHref = useMemo(() => RouteUtils.getTokenRoute(scope, address), [address, scope])
  const accountHref = useMemo(() => RouteUtils.getAccountRoute(scope, address), [address, scope])

  const selectedSummary = useMemo(() => {
    if (!files.length) return null
    const names = files.slice(0, 8).map(f => f.webkitRelativePath || f.name)
    const extra = files.length > 8 ? ` (+${files.length - 8} more)` : ''
    return `${files.length} file(s): ${names.join(', ')}${extra}`
  }, [files])

  const contractOptions: SelectOption[] = useMemo(() => {
    if (prepared?.kind !== 'solc-json') return []
    return preferProjectContracts(prepared.contracts).map(c => ({
      value: contractOptionValue(c),
      label: `${c.name} (${c.path})`,
    }))
  }, [prepared])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!files.length) {
        setPrepared(null)
        setContractKey('')
        return
      }
      try {
        const next = await prepareVerification(files)
        if (cancelled) return
        setPrepared(next)
        if (next.kind === 'solc-json') {
          const preferred = preferProjectContracts(next.contracts)
          setContractKey(preferred[0] ? contractOptionValue(preferred[0]) : '')
        } else {
          setContractKey('')
        }
      } catch (error) {
        if (cancelled) return
        setPrepared(null)
        setContractKey('')
        setResult({
          ok: false,
          message: error instanceof Error ? error.message : String(error),
        })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [files])

  const onPickFiles = useCallback((list: FileList | null) => {
    setResult(null)
    setFiles(list ? Array.from(list) : [])
  }, [])

  const onSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault()
      setResult(null)
      if (!files.length || !prepared) {
        setResult({ ok: false, message: t('contract.verification.verifyMissingFiles') })
        return
      }

      setBusy(true)
      try {
        const sourcify = getSourcifyServerUrl()
        let response: Response

        if (prepared.kind === 'solc-json') {
          const selected = parseContractOption(contractKey)
          if (!selected?.name) {
            setResult({ ok: false, message: t('contract.verification.verifyMissingContract') })
            return
          }
          const body = new FormData()
          body.append('address', address)
          body.append('chain', CHAIN_ID)
          body.append('compilerVersion', prepared.compilerVersion)
          body.append('contractName', selected.name)
          body.append(
            'files',
            new Blob([JSON.stringify(prepared.input)], { type: 'application/json' }),
            'SolcJsonInput.json',
          )
          response = await fetch(`${sourcify}/verify/solc-json`, {
            method: 'POST',
            body,
          })
        } else {
          if (!prepared.files.length) {
            setResult({ ok: false, message: t('contract.verification.verifyMissingFiles') })
            return
          }
          const body = new FormData()
          body.append('address', address)
          body.append('chain', CHAIN_ID)
          for (const item of prepared.files) {
            // Critical: third arg is the path Sourcify uses for source matching.
            body.append('files', item.blob, item.path)
          }
          response = await fetch(`${sourcify}/verify`, {
            method: 'POST',
            body,
          })
        }

        const payload = await response.json().catch(() => ({}))
        if (!response.ok) {
          const err =
            (typeof payload?.error === 'string' && payload.error) ||
            (typeof payload?.message === 'string' && payload.message) ||
            `HTTP ${response.status}`
          setResult({ ok: false, message: err })
          return
        }
        const status = payload?.result?.[0]?.status || payload?.result?.[0]?.runtimeMatch || 'perfect'
        setResult({
          ok: true,
          status: String(status),
          message: t('contract.verification.verifySuccess'),
        })
      } catch (error) {
        setResult({
          ok: false,
          message: error instanceof Error ? error.message : String(error),
        })
      } finally {
        setBusy(false)
      }
    },
    [address, contractKey, files.length, prepared, t],
  )

  return (
    <PageLayout>
      <Card variant="layout" className="max-w-3xl mx-auto">
        <CardHeader>
          <CardTitle>{t('contract.verification.verifyPageTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <p className="text-sm text-[#64748B]">{t('contract.verification.verifyPageIntro')}</p>

          <dl className="grid gap-2 text-sm">
            <div className="flex flex-wrap gap-2 justify-between border-t border-[#EEF1F5] pt-2">
              <dt className="text-[#64748B]">{t('common.smartContract')}</dt>
              <dd className="font-mono break-all">{address}</dd>
            </div>
            <div className="flex flex-wrap gap-2 justify-between border-t border-[#EEF1F5] pt-2">
              <dt className="text-[#64748B]">{t('layerPicker.chainId')}</dt>
              <dd>
                {CHAIN_ID} <span className="text-[#64748B]">(0x{Number(CHAIN_ID).toString(16)})</span>
              </dd>
            </div>
          </dl>

          <form className="space-y-4" onSubmit={onSubmit}>
            <div>
              <div className="block text-sm font-medium mb-2">{t('contract.verification.verifyFilesLabel')}</div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => filesInputRef.current?.click()}
                  disabled={busy}
                >
                  {t('contract.verification.verifyPickFiles')}
                </Button>
              </div>
              <input
                ref={filesInputRef}
                id="verify-files"
                type="file"
                multiple
                accept=".json,.sol,.vy,.txt"
                className="hidden"
                onChange={e => {
                  onPickFiles(e.target.files)
                  e.target.value = ''
                }}
              />
              {selectedSummary && <p className="mt-2 text-xs font-mono text-[#334155]">{selectedSummary}</p>}
              {prepared?.kind === 'solc-json' && (
                <p className="mt-2 text-xs text-amber-700">{t('contract.verification.verifySolcJsonHint')}</p>
              )}
              <p className="mt-2 text-xs text-[#64748B]">{t('contract.verification.verifyFilesHint')}</p>
            </div>

            {prepared?.kind === 'solc-json' && contractOptions.length > 0 && (
              <div>
                <div className="block text-sm font-medium mb-2">
                  {t('contract.verification.verifyContractName')}
                </div>
                <Select
                  className="w-full max-w-xl"
                  options={contractOptions}
                  value={contractKey}
                  handleChange={setContractKey}
                />
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Button type="submit" disabled={busy}>
                {busy ? t('contract.verification.verifying') : t('contract.verification.verifySubmit')}
              </Button>
              <Button type="button" variant="outline" asChild>
                <RouterLink to={tokenHref}>{t('contract.verification.backToToken')}</RouterLink>
              </Button>
              <Button type="button" variant="ghost" asChild>
                <RouterLink to={accountHref}>{t('contract.verification.backToContract')}</RouterLink>
              </Button>
            </div>
          </form>

          {result && (
            <div
              className={`rounded-md border px-3 py-2 text-sm ${
                result.ok
                  ? 'border-green-200 bg-green-50 text-green-800'
                  : 'border-red-200 bg-red-50 text-red-800'
              }`}
            >
              <p>{result.message}</p>
              {result.ok && result.status && (
                <p className="mt-1">
                  {t('contract.verification.verifyMatchStatus', { status: result.status })}
                </p>
              )}
              {result.ok && (
                <p className="mt-2">
                  <Link asChild className="font-semibold underline">
                    <RouterLink to={tokenHref}>{t('contract.verification.backToToken')}</RouterLink>
                  </Link>
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </PageLayout>
  )
}
